import React, { useState } from "react";
import { Collapse, Spin, Button, Card, Tooltip, Typography } from "antd";
import { 
  WarningOutlined, 
  CopyOutlined,
  DownOutlined,
  UpOutlined,
  TagOutlined,
  EyeOutlined
} from "@ant-design/icons";

const { Text } = Typography;
import { MedicalCondition, Icd10Code, TextSnippet } from "../../types/codeIdentifier";
import { useCopyUtilities } from "../../hooks/useCopyUtilities";
import ICDCodePills from "./ICDCodePills";

interface MedicalConditionCategoricalViewProps {
  finalEvaluation: MedicalCondition[];
  loadingFinalEvaluation: boolean;
  onHighlightCode?: (code: Icd10Code, snippets: TextSnippet[]) => void;
  activeHighlightCode?: string | null;
}

const MedicalConditionCategoricalView: React.FC<MedicalConditionCategoricalViewProps> = ({
  finalEvaluation,
  loadingFinalEvaluation,
  onHighlightCode,
  activeHighlightCode
}) => {
  const [activeKeys, setActiveKeys] = useState<(string | number)[]>([]);
  const { copyToClipboard, getUniqueValidCodesCount, copyAllCodes } = useCopyUtilities(finalEvaluation);

  // Helper function to get all codes from a condition (handles both single and multiple codes)
  const getCodesFromCondition = (condition: MedicalCondition): Icd10Code[] => {
    if (condition.best_icd10_codes && condition.best_icd10_codes.length > 0) {
      return condition.best_icd10_codes;
    }
    // Fallback for backward compatibility
    if ((condition as any).best_icd10_code) {
      return [(condition as any).best_icd10_code];
    }
    return [];
  };

  // Helper function to render a single code line
  const renderCodeLine = (code: Icd10Code, index: number, total: number, condition: MedicalCondition) => {
    // Handle special cases
    const isNoCode = code.code === "No Code";
    const isNonBillable = code.short_description === "Non Billable";
    
    // Determine code color based on type
    const codeColor = isNoCode ? '#999' : isNonBillable ? '#ff4d4f' : '#1890ff';
    
    return (
      <div key={`${code.code}-${index}`} style={{ marginBottom: total > 1 && index < total - 1 ? '8px' : '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <Text style={{ fontSize: '14px' }}>
            Code: <strong style={{ color: codeColor }}>{code.code}</strong>
          </Text>
          {!isNoCode && (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {onHighlightCode && condition.snippets && condition.snippets.length > 0 && (
                <Tooltip title={activeHighlightCode === code.code ? "Clear highlights" : "Highlight in PDF"}>
                  <Button
                    type={activeHighlightCode === code.code ? "primary" : "text"}
                    size="small"
                    icon={<EyeOutlined style={{ fontSize: '16px' }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onHighlightCode(code, condition.snippets || []);
                    }}
                    style={{
                      padding: '2px 6px',
                      height: '28px',
                      minWidth: '28px',
                      color: activeHighlightCode === code.code ? '#fff' : '#52c41a',
                      backgroundColor: activeHighlightCode === code.code ? '#52c41a' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title="Copy code only">
                <Button
                  type="text"
                  size="small"
                  icon={<TagOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(code.code, `Code ${code.code}`);
                  }}
                  style={{
                    padding: '2px 4px',
                    height: '20px',
                    minWidth: '20px',
                    color: codeColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px'
                  }}
                />
              </Tooltip>
              <Tooltip title="Copy code and description">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(
                      `${code.code} - ${code.short_description}`,
                      'Code and description'
                    );
                  }}
                  style={{
                    padding: '2px 4px',
                    height: '20px',
                    minWidth: '20px',
                    color: codeColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Tooltip>
            </div>
          )}
        </div>
        {code.short_description && (
          <div style={{ marginBottom: '4px' }}>
            <Text style={{ fontSize: '12px', color: isNonBillable ? '#ff4d4f' : '#666', fontStyle: 'italic' }}>
              {code.short_description}
            </Text>
          </div>
        )}
        <div>
          <ICDCodePills 
            clinicalCategory={code.clinical_category} 
            ntaPoints={code.nta_points}
            isSLP={code.is_slp}
            size="small"
          />
        </div>
      </div>
    );
  };

  const handleChange = (keys: React.Key | React.Key[]) => {
    const normalizedKeys = Array.isArray(keys)
      ? keys.map((key) => key.toString())
      : [keys.toString()];
    setActiveKeys(normalizedKeys);
  };

  // Custom expand icon with enhanced visibility
  const customExpandIcon = ({ isActive }: { isActive?: boolean }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: isActive ? '#1890ff' : '#f0f0f0',
      border: `2px solid ${isActive ? '#1890ff' : '#d9d9d9'}`,
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    }}
    onMouseEnter={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = '#e6f7ff';
        e.currentTarget.style.borderColor = '#91d5ff';
      }
    }}
    onMouseLeave={(e) => {
      if (!isActive) {
        e.currentTarget.style.backgroundColor = '#f0f0f0';
        e.currentTarget.style.borderColor = '#d9d9d9';
      }
    }}
    >
      {isActive ? (
        <UpOutlined style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }} />
      ) : (
        <DownOutlined style={{ color: '#666', fontSize: '14px', fontWeight: 'bold' }} />
      )}
    </div>
  );

  const getGroupedFinalEvaluation = () => {
    const grouped = finalEvaluation.reduce((acc, condition) => {
      const category = condition.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(condition);
      return acc;
    }, {} as Record<string, MedicalCondition[]>);

    // Sort categories alphabetically
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  if (loadingFinalEvaluation) {
    return (
      <div style={{ textAlign: "center", padding: "24px" }}>
        <Spin />
      </div>
    );
  }

  if (finalEvaluation.length === 0) {
    return (
      <p style={{ textAlign: "center", color: "#999" }}>No data available</p>
    );
  }

  return (
    <>
      <style>
        {`
          .enhanced-collapse .ant-collapse-item {
            transition: all 0.3s ease;
          }
          
          .enhanced-collapse .ant-collapse-item:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transform: translateY(-1px);
          }
          
          .enhanced-collapse .ant-collapse-header {
            transition: all 0.3s ease;
          }
          
          .enhanced-collapse .ant-collapse-header:hover {
            background-color: #f0f9ff !important;
          }
          
          .enhanced-collapse .ant-collapse-item-active .ant-collapse-header {
            background-color: #e6f7ff !important;
          }
          
          .enhanced-collapse .ant-collapse-expand-icon {
            padding-right: 16px !important;
            display: flex !important;
            align-items: center !important;
            height: 100% !important;
          }
        `}
      </style>

      {/* Subtle Copy All Unique Codes option */}
      <div style={{ marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={copyAllCodes}
          disabled={getUniqueValidCodesCount() === 0}
          style={{ 
            color: '#8c8c8c',
            fontSize: '12px',
            height: '24px',
            padding: '0 8px'
          }}
        >
          Copy all unique codes ({getUniqueValidCodesCount()})
        </Button>
      </div>

      {getGroupedFinalEvaluation().map(([category, conditions]) => (
        <div key={category} style={{ marginBottom: '24px' }}>
          <h3 style={{ 
            marginBottom: '16px', 
            padding: '8px 16px',
            background: '#f0f2f5',
            borderRadius: '4px',
            color: '#1f1f1f'
          }}>
            {category}
          </h3>
          {conditions.map((item, index) => (
            <Collapse 
              key={`${category}-${index}`}
              activeKey={activeKeys} 
              onChange={handleChange} 
              expandIcon={customExpandIcon}
              style={{
                background: 'transparent',
                marginBottom: '12px',
              }}
              className="enhanced-collapse"
              items={[{
                key: `${category}-${index}`,
                label: (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '4px 0',
                    cursor: 'pointer'
                  }}>
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ fontSize: '16px', color: '#1f1f1f' }}>
                        {item.condition_name}
                      </Text>
                      <div style={{ marginTop: '6px' }}>
                        {/* Codes section - handle multiple codes */}
                        {(() => {
                          const codes = getCodesFromCondition(item);
                          if (codes.length === 0) {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <Text style={{ fontSize: '14px' }}>
                                  Code: <strong style={{ color: '#999' }}>N/A</strong>
                                </Text>
                              </div>
                            );
                          }
                          return codes.map((code, index) => renderCodeLine(code, index, codes.length, item));
                        })()}
                      </div>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px', marginLeft: '16px' }}>
                      Click to view details
                    </Text>
                  </div>
                ),
                style: {
                  border: '1px solid #e8e8e8',
                  borderRadius: '6px',
                },
                children: (
                  <div>
                    <p>
                      <b>Condition Description:</b> <br />
                      {item.condition_description}
                    </p>
                    {item.snippets && item.snippets.length > 0 && (
                      <Card type="inner" title="Provider Notes" style={{ marginBottom: 16 }}>
                        {item.snippets.map((snippet, i) => (
                          <p key={i} style={{ 
                            backgroundColor: '#f5f5f5',
                            padding: '8px',
                            borderRadius: '4px',
                            marginBottom: '8px',
                            fontStyle: 'italic'
                          }}>
                            "{snippet.text}"
                          </p>
                        ))}
                      </Card>
                    )}
                    {(() => {
                      const codes = getCodesFromCondition(item);
                      if (codes.length === 0) {
                        return null;
                      }
                      return codes.map((code, index) => (
                        <Card 
                          key={`expanded-${code.code}-${index}`}
                          type="inner" 
                          title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span>Code: {code.code}</span>
                                <ICDCodePills 
                                  clinicalCategory={code.clinical_category} 
                                  ntaPoints={code.nta_points}
                                  isSLP={code.is_slp}
                                  size="small"
                                />
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <Tooltip title="Copy code only">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<TagOutlined />}
                                    onClick={() => copyToClipboard(code.code, `Code ${code.code}`)}
                                    style={{ color: '#1890ff', fontSize: '12px' }}
                                  />
                                </Tooltip>
                                <Tooltip title="Copy code and description">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => copyToClipboard(
                                      `${code.code} - ${code.short_description}`,
                                      'Code and description'
                                    )}
                                    style={{ color: '#1890ff' }}
                                  />
                                </Tooltip>
                              </div>
                            </div>
                          }
                          style={{ marginBottom: index < codes.length - 1 ? '16px' : '0' }}
                        >
                          <p>
                            <strong>Description:</strong> {code.short_description}
                          </p>
                          {code.reasoning && (
                            <p>
                              <strong>Reasoning:</strong> {code.reasoning}
                            </p>
                          )}
                        </Card>
                      ));
                    })()}
                  </div>
                )
              }]}
            />
          ))}
        </div>
      ))}
    </>
  );
};

export default MedicalConditionCategoricalView; 